<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div v-if="error" class="box error">
    <h2> {{ $t('error') }} </h2>
    <p>{{ $t('error-loading-image') }}</p>
    <p v-if="errorBadImageProject">{{ $t('error-loading-image-bad-project') }}</p>
  </div>
  <div v-else class="cytomine-viewer">

    <image-selector />

    <div class="viewer-main">
      <viewer-toolbar v-if="activeImageReady" :active-image-index="activeImageIndex"
        :active-panel="activePanel" :active-overlay="activeOverlay" :active-overlays="activeOverlays" :config-ui="configUI"
        :is-fullscreen="isFullscreen" :is-audit-log-panel-displayed="isAuditLogPanelDisplayed"
        :can-open-image-panel="activeImageReady"
        @run-command="callActiveImage" @toggle-panel="toggleActivePanel" @toggle-overlay="toggleActiveOverlay"
        @toggle-fullscreen="toggleFullscreen" @open-modal="openViewerModal" />

      <div class="maps-wrapper">
        <div class="map-cell" v-for="(cell, i) in cells" :key="i"
          :style="`height:${elementHeight}%; width:${elementWidth}%;`"
          :class="{ highlighted: cell && cell.highlighted, active: cell && cell.index === activeImageIndex }"
          @pointerdown.capture="cell && setActiveImage(cell.index)">
          <cytomine-image v-if="cell && cell.image && cell.slices" :index="cell.index"
            :command="cell.index === activeImageIndex ? activeImageCommand : null"
            :key="`${cell.index}-${cell.image.id}`" @close="closeMap(cell.index)" />
        </div>
      </div>

      <viewer-side-panel v-if="activeImageReady && activeOverlays.algorithm" active-panel="algorithm" position="left"
        :active-image-index="activeImageIndex" :project="project" @close="closeActiveOverlay('algorithm')" />

      <viewer-side-panel v-if="activeImageReady && rightOverlayPanel" :active-panel="rightOverlayPanel" position="right"
        :active-image-index="activeImageIndex" :project="project" @close="closeActiveOverlay(rightOverlayPanel)" />

    </div>

    <share-project-modal :active="shareModalActive" :project="project" @update:active="shareModalActive = $event" />

    <b-modal :active.sync="annotationExportModalActive" has-modal-card>
      <div class="modal-card annotation-export-modal">
        <header class="modal-card-head">
          <div class="annotation-export-title">
            <span class="icon annotation-export-title-icon">
              <i class="fas fa-file-export"></i>
            </span>
            <p class="modal-card-title">{{ $t('export-annotations') }}</p>
          </div>
        </header>
        <section class="modal-card-body">
          <div class="annotation-export-group">
            <div class="annotation-export-label">{{ $t('format') }}</div>
            <b-field class="annotation-export-format radio-buttons-fullwidth">
              <b-radio-button v-model="annotationExportFormat" native-value="csv" type="is-link">
                CSV
              </b-radio-button>
              <b-radio-button v-model="annotationExportFormat" native-value="xls" type="is-link">
                XLS
              </b-radio-button>
            </b-field>
          </div>
          <div class="annotation-export-group">
            <div class="annotation-export-label">{{ $t('annotation-type') }}</div>
            <div class="annotation-export-type-grid">
              <b-radio class="annotation-export-type-card" :class="{ 'is-selected': annotationExportType === 'user' }"
                v-model="annotationExportType" native-value="user">
                <span class="annotation-export-type-content">
                  <span class="icon">
                    <i class="fas fa-user-edit"></i>
                  </span>
                  <span>{{ $t('user-annotations') }}</span>
                </span>
              </b-radio>
              <b-radio class="annotation-export-type-card" :class="{ 'is-selected': annotationExportType === 'algo' }"
                v-model="annotationExportType" native-value="algo">
                <span class="annotation-export-type-content">
                  <span class="icon">
                    <i class="fas fa-robot"></i>
                  </span>
                  <span>{{ $t('algo-annotations') }}</span>
                </span>
              </b-radio>
              <b-radio class="annotation-export-type-card" :class="{ 'is-selected': annotationExportType === 'all' }"
                v-model="annotationExportType" native-value="all">
                <span class="annotation-export-type-content">
                  <span class="icon">
                    <i class="fas fa-layer-group"></i>
                  </span>
                  <span>{{ $t('all') }}</span>
                </span>
              </b-radio>
            </div>
          </div>
          <b-message v-if="showAlgoXlsExportWarning" class="annotation-export-warning" type="is-warning"
            size="is-small" has-icon>
            {{ $t('warning-algo-xls-wkt-truncated') }}
          </b-message>
        </section>
        <footer class="modal-card-foot">
          <button class="button" type="button" @click="annotationExportModalActive = false">
            {{ $t('button-cancel') }}
          </button>
          <button class="button is-link" type="button" :class="{ 'is-loading': annotationExportLoading }"
            :disabled="annotationExportLoading" @click="exportAnnotations">
            {{ $t('button-confirm') }}
          </button>
        </footer>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { get } from '@/utils/store-helpers';
import { appendShortTermToken } from '@/utils/token-utils.js';

import CytomineImage from './CytomineImage';
import ImageSelector from './ImageSelector';
import ViewerToolbar from './ViewerToolbar';
import ViewerSidePanel from './ViewerSidePanel';

import viewerModuleModel from '@/store/modules/project_modules/viewer';

import shortcuts from '@/utils/shortcuts.js';
import { findShortcutKey, preventShortcutEvent } from '@/utils/shortkey-events';

import { ImageInstance, SliceInstance, Annotation, AnnotationCollection } from '@/api';

export default {
  name: 'cytomine-viewer',
  components: {
    CytomineImage,
    ImageSelector,
    ViewerToolbar,
    ViewerSidePanel,
    ShareProjectModal: () => import('@/components/project/ShareProjectModal.vue'),
  },
  data() {
    return {
      error: false,
      errorBadImageProject: false,
      loading: true,
      idViewer: null,
      isFullscreen: false,
      shareModalActive: false,
      annotationExportModalActive: false,
      annotationExportFormat: 'csv',
      annotationExportType: 'user',
      annotationExportLoading: false,
      activeImageCommand: null,
      activeImageCommandNonce: 0,
    };
  },
  computed: {
    project: get('currentProject/project'),
    currentUser: get('currentUser/user'),
    shortTermToken: get('currentUser/shortTermToken'),
    configUI: get('currentProject/configUI'),
    viewers() {
      let projectModule = this.$store.getters.projectModule(this.project?.id);
      return projectModule?.viewers || {};
    },
    idImages() {
      return this.$route.params.idImages.split('-');
    },
    idSlices() {
      return (this.$route.params.idSlices) ? this.$route.params.idSlices.split('-') : [];
    },
    paramIdViewer() {
      return this.$route.query.viewer;
    },
    viewerModule() {
      return this.$store.getters['currentProject/viewerModulePath'];
    },
    viewer() {
      return this.$store.getters.viewerModule({
        idProject: this.project?.id,
        idViewer: this.idViewer
      });
    },
    indexImages() {
      return this.viewer ? Object.keys(this.viewer.images) : [];
    },
    nbImages() {
      return this.indexImages.length;
    },
    activeImageIndex() {
      return this.viewer && this.viewer.activeImage !== null && this.viewer.activeImage !== undefined
        ? String(this.viewer.activeImage)
        : null;
    },
    activeImageWrapper() {
      if (!this.viewer || this.activeImageIndex === null || this.activeImageIndex === undefined) {
        return null;
      }
      return this.viewer.images[this.activeImageIndex] || null;
    },
    activeImage() {
      return this.activeImageWrapper ? this.activeImageWrapper.imageInstance : null;
    },
    activeImageReady() {
      return Boolean(this.activeImageWrapper && this.activeImageWrapper.imageInstance && this.activeImageWrapper.activeSlices);
    },
    activeImageModule() {
      return this.activeImageIndex === null || this.activeImageIndex === undefined
        ? null
        : this.$store.getters['currentProject/imageModulePath'](this.activeImageIndex);
    },
    activePanel() {
      return this.activeImageWrapper ? this.activeImageWrapper.activePanel : null;
    },
    activeOverlay() {
      return this.activeImageWrapper ? this.activeImageWrapper.activeOverlay : null;
    },
    activeOverlays() {
      return this.activeImageWrapper ? this.activeImageWrapper.activeOverlays || {} : {};
    },
    rightOverlayPanel() {
      if (this.activeOverlays.report) {
        return 'report';
      }
      if (this.activeOverlays['audit-log']) {
        return 'audit-log';
      }
      if (['report', 'audit-log'].includes(this.activeOverlay)) {
        return this.activeOverlay;
      }
      return null;
    },
    showAlgoXlsExportWarning() {
      return this.annotationExportFormat === 'xls' && this.isAlgoAnnotationExport;
    },
    isAlgoAnnotationExport() {
      return ['algo', 'all'].includes(this.annotationExportType);
    },
    isAuditLogPanelDisplayed() {
      let value = this.configUI['project-explore-annotation-audit-logs'];
      return value === undefined || value === null || value;
    },
    nbHorizontalCells() {
      return Math.ceil(Math.sqrt(this.nbImages));
    },
    nbVerticalCells() {
      return this.nbHorizontalCells ? Math.ceil(this.nbImages / this.nbHorizontalCells) : 0;
    },
    cells() {
      let cells = new Array(this.nbHorizontalCells * this.nbVerticalCells);
      for (let i = 0; i < this.nbImages; i++) {
        let index = this.indexImages[i];
        let image = this.viewer.images[index].imageInstance;
        let slices = this.viewer.images[index].activeSlices;
        let highlighted = (this.viewer.images[index].view) ? this.viewer.images[index].view.highlighted : false;
        cells[i] = { index, image, slices, highlighted };
      }
      return cells;
    },
    elementHeight() {
      return 100 / this.nbVerticalCells;
    },
    elementWidth() {
      return 100 / this.nbHorizontalCells;
    },
    shortkeysMapping() {
      let allowed = ['nav-next-image', 'nav-previous-image', 'nav-next-slice', 'nav-previous-slice', 'nav-next-t',
        'nav-previous-t', 'nav-next-c', 'nav-previous-c', 'nav-first-slice', 'nav-last-slice', 'nav-first-t',
        'nav-last-t', 'nav-first-z', 'nav-last-z', 'nav-first-c', 'nav-last-c', 'nav-next-image-in-group',
        'nav-previous-image-in-group', 'nav-next-annot-link', 'nav-previous-annot-link',
        'tool-select', 'tool-point', 'tool-line', 'tool-freehand-line', 'tool-rectangle', 'tool-circle', 'tool-polygon',
        'tool-freehand-polygon', 'tool-screenshot', 'tool-fill', 'tool-correct-add', 'tool-correct-remove', 'tool-modify', 'tool-rescale',
        'tool-move', 'tool-rotate', 'tool-delete', 'tool-undo', 'tool-redo', 'tool-review-accept', 'tool-review-reject',
        'toggle-review-layer', 'toggle-all-review-layer', 'toggle-selected-layers', 'toggle-all-selected-layers',
        'tool-go-to-slice-t', 'tool-go-to-slice-z', 'tool-go-to-slice-c', 'toggle-information',
        'toggle-zoom', 'toggle-filters', 'toggle-layers', 'toggle-ontology', 'toggle-properties',
        'toggle-review', 'toggle-annotations', 'toggle-current', 'toggle-add-image', 'toggle-link',
        'nav-next-z', 'nav-previous-z', 'tool-copy', 'tool-paste', 'tool-review-reject', 'tool-review-toggle',
        'tool-go-to-slice-t', 'tool-go-to-slice-z', 'tool-go-to-slice-c', 'toggle-all-information', 'toggle-all-zoom',
        'toggle-all-filters', 'toggle-all-layers', 'toggle-all-ontology', 'toggle-all-properties',
        'toggle-all-review', 'toggle-all-annotations',
        'toggle-all-current', 'toggle-all-link'];

      return Object.keys(shortcuts).filter(key => allowed.includes(key.replace('viewer-', ''))).reduce((object, key) => {
        object[key.replace('viewer-', '')] = shortcuts[key];
        return object;
      }, {});
    },
  },
  watch: {
    paramIdViewer() {
      this.findIdViewer();
    },
    idViewer(_, old) {
      if (old) {
        this.loading = true;
        this.loadViewer();
      }
    },
    viewer() {
      if (!this.viewer) {
        console.log('Viewer closed from external source');
        this.$router.push(`/project/${this.$route.params.idProject}`);
      }
    },
    nbImages() {
      this.$eventBus.$emit('updateMapSize');
    }
  },
  methods: {
    callActiveImage(methodName) {
      this.activeImageCommand = {
        name: methodName,
        nonce: ++this.activeImageCommandNonce
      };
    },
    toggleActivePanel(panel) {
      if (this.activeImageModule) {
        this.$store.commit(this.activeImageModule + 'togglePanel', panel);
      }
    },
    toggleActiveOverlay(overlay) {
      if (this.activeImageModule) {
        this.$store.commit(this.activeImageModule + 'toggleOverlay', overlay);
      }
    },
    closeActiveOverlay(overlay) {
      if (this.activeImageModule) {
        this.$store.commit(this.activeImageModule + 'closeOverlay', overlay);
      }
    },
    openViewerModal(modal) {
      switch (modal) {
        case 'share':
          this.shareModalActive = true;
          break;
        case 'export':
          this.annotationExportModalActive = true;
          break;
      }
    },
    exportAnnotations() {
      if (!this.activeImage) {
        return;
      }
      this.annotationExportLoading = true;
      try {
        let exportParams = {
          project: this.project.id,
          images: [this.activeImage.id],
          reviewed: false,
          annotationType: this.annotationExportType
        };
        if (this.isAlgoAnnotationExport) {
          exportParams.aiVisualizationType = 'all';
        }
        let collection = new AnnotationCollection(exportParams);
        window.location.assign(appendShortTermToken(collection.getDownloadURL(this.annotationExportFormat), this.shortTermToken));
        this.annotationExportModalActive = false;
      } catch (error) {
        console.log(error);
        this.$notify({ type: 'error', text: this.$t('notif-error-export-annotations') });
      } finally {
        this.annotationExportLoading = false;
      }
    },
    toggleFullscreen() {
      const element = this.$el;
      if (!document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !document.mozFullScreenElement &&
        !document.msFullscreenElement) {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement);
    },
    findIdViewer() {
      if (this.paramIdViewer) {
        this.idViewer = this.paramIdViewer;
        return;
      }

      for (let id in this.viewers) {
        // if viewer containing the targetted images, and only them, store its id
        let imagesViewer = Object.values(this.viewers[id].images).map(img => img.imageInstance.id).join('-');
        if (imagesViewer === this.$route.params.idImages) {
          this.idViewer = id;
          return;
        }
      }

      this.idViewer = Math.random().toString(36).substr(2, 9);
    },
    setActiveImage(index) {
      this.$store.commit(this.viewerModule + 'setActiveImage', index);
    },

    closeMap(index) {
      if (this.nbImages === 1) {
        this.$store.unregisterModule(['projects', this.project.id, 'viewers', this.idViewer]);
        this.$router.push(`/projects`);
      } else {
        if (this.activeImageIndex === index) {
          let nextIndex = this.indexImages.find(imageIndex => imageIndex !== index);
          if (nextIndex !== undefined) {
            this.setActiveImage(nextIndex);
          }
        }
        this.$store.dispatch(this.viewerModule + 'removeImage', index);
      }
    },

    async loadViewer() {
      try {
        this.$store.commit('currentProject/setCurrentViewer', this.idViewer);
        if (!this.viewer) {
          await this.$store.dispatch('ensureProjectModule', this.project.id);
          this.$store.registerModule(['projects', this.project.id, 'viewers', this.idViewer], viewerModuleModel);

          // List of unique images (prevent to fetch it multiple times)
          const uniqueIdImages = [...new Set(this.idImages)];
          let uniqueImages = {};
          await Promise.all(uniqueIdImages.map(async id => {
            uniqueImages[id] = await ImageInstance.fetch(id);
          }));

          // Ensure images are in the right project
          const nbWrongProjectImages = Object.values(uniqueImages).filter(image =>
            image.project !== this.project.id
          ).length;
          if (nbWrongProjectImages > 0) {
            this.errorBadImageProject = true;
            throw new Error('Some images are not from this project');
          }

          // Register images in the viewer, in right order
          let indexedImages = {};
          this.idImages.map(id => {
            indexedImages[this.viewer.indexNextImage] = uniqueImages[id];
            this.$store.dispatch(this.viewerModule + 'registerImage');
          });

          // For each image, initialize them asynchronously, and fetch corresponding slices
          await Promise.all(Object.entries(indexedImages).map(async ([index, image]) => {
            const position = this.idImages.indexOf(String(image.id));
            let idSlices = this.idSlices[position];

            let slices;
            if (idSlices) {
              idSlices = [...new Set(idSlices.split(':'))];
              slices = await Promise.all(idSlices.map(async id => await SliceInstance.fetch(id)));

              // Ensure slices are in the right project/image
              const z = slices[0].zStack;
              const t = slices[0].time;
              const nbWrongSlices = slices.filter(slice =>
                slice.image !== image.id || slice.zStack !== z || slice.time !== t
              ).length;
              if (nbWrongSlices > 0) {
                this.errorBadImageProject = true;
                throw new Error('Some slices are not from this project or cannot be displayed together');
              }
            } else {
              slices = [await image.fetchReferenceSlice()];
            }
            await this.$store.dispatch(`${this.viewerModule}images/${index}/initialize`, { image, slices });
          }));

          let images = {};
          //don't fetch multiple times the same image.
          let idImages = [...new Set(this.idImages)];
          await Promise.all(idImages.map(async id => {
            let image = await ImageInstance.fetch(id);
            images[id] = image;
          }));
          const imagesNotInCurrentProject = Object.values(images).filter(image => image.project !== this.project.id);
          if (imagesNotInCurrentProject.length > 0) {
            this.errorBadImageProject = true;
            throw new Error('Some images are not from this project');
          }
        } else {
          await this.$store.dispatch(this.viewerModule + 'refreshData');
        }
        this.loading = false;
      } catch (err) {
        console.log(err);
        this.error = true;
      }
    },

    async selectAnnotationHandler({ index, annot, center = false, fromAuditLog = false }) {
      try {
        if (index && annot.image !== this.viewer.images[index].imageInstance.id) {
          annot = await Annotation.fetch(annot.id);
          let [image, slice] = await Promise.all([
            ImageInstance.fetch(annot.image),
            SliceInstance.fetch(annot.slice)
          ]);
          this.$store.commit(`${this.viewerModule}images/${index}/setRoutedAnnotation`, annot);
          await this.$store.dispatch(`${this.viewerModule}images/${index}/setImageInstance`, { image, slices: [slice] });
        } else if (index === null) {
          annot = await Annotation.fetch(annot.id);
          if (this.idImages.includes(String(annot.image))) {
            let index = this.cells.find(cell => cell.image.id === annot.image).index;
            this.$eventBus.$emit('selectAnnotation', { index, annot, center, fromAuditLog });
          } else {
            let [image, slice] = await Promise.all([
              ImageInstance.fetch(annot.image),
              SliceInstance.fetch(annot.slice)
            ]);
            await this.$store.dispatch(this.viewerModule + 'addImage', { image, slices: [slice], annot });
          }
        }
      } catch (err) {
        console.log(err);
        if (fromAuditLog) {
          this.$notify({ type: 'error', text: this.$t('notif-error-target-annotation-deleted') });
        }
        this.error = true;
      }
    },

    handleShortcutKeydown(event) {
      if (findShortcutKey(event, this.shortkeysMapping)) {
        preventShortcutEvent(event);
      }
    },

    handleShortcutKeyup(event) {
      const key = findShortcutKey(event, this.shortkeysMapping);
      if (key) {
        preventShortcutEvent(event);
        this.$eventBus.$emit('shortkeyEvent', key);
      }
    }
  },
  async created() {
    this.findIdViewer();
    await this.loadViewer();
  },
  mounted() {
    this.$eventBus.$on('selectAnnotation', this.selectAnnotationHandler);
    document.addEventListener('keydown', this.handleShortcutKeydown, true);
    document.addEventListener('keyup', this.handleShortcutKeyup, true);
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    this.$eventBus.$off('selectAnnotation', this.selectAnnotationHandler);
    document.removeEventListener('keydown', this.handleShortcutKeydown, true);
    document.removeEventListener('keyup', this.handleShortcutKeyup, true);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.cytomine-viewer {
  display: flex;
  height: 100%;
}

.viewer-main {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  position: relative;
  background-color: $dark-bg-canvas;
}

.maps-wrapper {
  flex: 1;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  background-color: $dark-bg-canvas;
}

.map-cell {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(95, 215, 232, 0.09);
  box-sizing: border-box;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  &.highlighted {
    border: 3px solid $primary;
    box-shadow: inset 0 0 0 1px rgba($primary, 0.45), 0 0 0 1px rgba($primary, 0.2);
  }

  &.active {
    border: 2px solid $primary;
    box-shadow: inset 0 0 0 2px rgba($primary, 0.22), 0 0 0 1px rgba($primary, 0.28);
  }
}

.annotation-export-modal {
  width: 32rem;
  max-width: calc(100vw - 2rem);

  .modal-card-head {
    padding: 1.15rem 1.25rem;
  }

  .annotation-export-title {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    min-width: 0;
  }

  .annotation-export-title-icon {
    background: rgba($primary, 0.13);
    border: 1px solid rgba($primary, 0.26);
    border-radius: 8px;
    color: $primary;
    flex: 0 0 2rem;
    height: 2rem;
    width: 2rem;
  }

  .modal-card-title {
    font-size: 1.15rem;
    line-height: 1.2;
  }

  .modal-card-body {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    padding: 1.25rem;
  }

  .field {
    margin-bottom: 0;
  }

  .annotation-export-group {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .annotation-export-label {
    color: $dark-text-secondary;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .annotation-export-format {
    display: flex;

    :deep(.field.has-addons) {
      display: flex;
      width: 100%;
    }

    :deep(.control) {
      flex: 1;
    }

    :deep(.button) {
      justify-content: center;
      width: 100%;
    }
  }

  .annotation-export-type-grid {
    display: grid;
    gap: 0.55rem;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .annotation-export-type-card {
    align-items: center;
    background: rgba(222, 238, 255, 0.04);
    border: 1px solid rgba(190, 214, 238, 0.13);
    border-radius: 8px;
    color: $dark-text-secondary;
    cursor: pointer;
    display: flex;
    min-height: 4rem;
    padding: 0.72rem;
    transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease;
    width: 100%;

    &:hover {
      background: rgba($primary, 0.08);
      border-color: rgba($primary, 0.25);
      color: $dark-text-primary;
    }

    &.is-selected {
      background: rgba($primary, 0.12);
      border-color: rgba($primary, 0.52);
      color: $dark-text-primary;
    }

    :deep(.control-label) {
      display: flex;
      width: 100%;
    }

    :deep(input) {
      flex: 0 0 auto;
      margin-top: 0.15rem;
    }
  }

  .annotation-export-type-content {
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
    text-align: center;

    .icon {
      color: $primary;
    }

    span:last-child {
      font-size: 0.82rem;
      font-weight: 600;
      line-height: 1.2;
      overflow-wrap: anywhere;
    }
  }

  .modal-card-foot {
    padding: 1rem 1.25rem;
  }

  .annotation-export-warning {
    :deep(.message-body) {
      background: rgba(255, 179, 71, 0.14);
      border-color: #ffb347;
      color: $dark-text-primary;
    }

    :deep(.icon) {
      color: #ffb347;
    }
  }
}

.hidden {
  display: none;
}

.no-result {
  margin: 2em;
  color: $dark-text-primary;
}
</style>
